import { OnboardingOutSchema } from "@/api/client";
import { handleError } from "@/api/handleError";
import { PINIA_PERSIST_OPTIONS } from "@/helpers/constants";
import { getListOfOnboardingStage } from "@/helpers/personalization";
import { ScriptInfo } from "@/helpers/types/callScript";
import { OnboardingList, VoiceAccent } from "@/helpers/types/personalization";
import { defineStore } from "pinia";
import { useBusinessStore } from "./businesses.store";
import { useNumberStore } from "./numbers.store";
import { Status } from "./types";
import { useUtilsStore } from "./utils.store";

interface PersonalizationState {
  userOnboardingStatus?: OnboardingOutSchema | null;
  callScript?: ScriptInfo | null;
  openManageScriptDrawer: boolean;
  accents: VoiceAccent[] | null;
  greetingActivatingProgress: number;
  isCompletingSetupFlow: boolean;
  welcomeGreetingList: unknown;
  teamMembers?: unknown;
  isPublishingAudio: boolean;
  isPublished: boolean;
  verificationBannerTimer: number;
  hasFilledTypeForm: boolean;
  pendingScreenTotalSeconds: number;
}
export const usePersonalizeStore = defineStore("personalization", {
  state: (): PersonalizationState => ({
    userOnboardingStatus: null,
    callScript: null,
    openManageScriptDrawer: false,
    accents: null,
    greetingActivatingProgress: 0,
    isCompletingSetupFlow: false,
    welcomeGreetingList: [],
    teamMembers: [],
    isPublishingAudio: false,
    isPublished: false,
    verificationBannerTimer: 0,
    hasFilledTypeForm: false,
    pendingScreenTotalSeconds: 0,
  }),

  getters: {
    orderedStatus(state): Status[] {
      const businessStore = useBusinessStore();

      return this.statuses.map((status: Status) => ({
        ...status,
        completed:
          status.value === "verify_nin" // check for nin verification through user object
            ? !businessStore.activeBusiness?.verified
              ? false
              : true
            : state.userOnboardingStatus?.[
                status.value as keyof OnboardingList
              ] || false,
      }));
    },

    completedTasksCount(state): number {
      if (state.userOnboardingStatus) {
        return this.orderedStatus?.filter((item) => item.completed === true)
          .length;
      } else {
        return 0;
      }
    },

    statusLength(): number {
      return this.orderedStatus?.length ?? 0;
    },
    completedTaskPercentage(): number {
      return (this.completedTasksCount / this.statusLength) * 100;
    },
    statuses(): Status[] {
      const businessStore = useBusinessStore();

      return [
        {
          title: "Verify your identity",
          subtitle: `We need to make sure of your identity to use our service.${
            !useNumberStore().requiresNINVerification
              ? " You will need your NIN and a valid ID"
              : ""
          }`,
          buttonTitle: "Verify",
          img: "/assets/Manager/setup-status/verify.svg",
          to: "/managers/profile-update",
          value: "verify_nin",
        },
        ...(!useUtilsStore().isSingleGoGlobalUser
          ? [
              {
                title: "Invite your team",
                subtitle:
                  "Invite everyone that needs to make or receive calls.",
                buttonTitle: "Invite team",
                img: "/assets/Manager/setup-status/invite-team.svg",
                value: "has_invited_team_member",
                to: "/managers/team",
              },
            ]
          : []),
        ...(businessStore.activeProfile?.business?.is_go_global
          ? []
          : [
              {
                title: "Set up welcome greeting",
                subtitle:
                  "Customize what your customers hear when they call your number during business hours, before it rings.",
                buttonTitle: "Start",
                img: "/assets/Manager/setup-status/call-flow.svg",
                to: "/managers/incoming-flow",
                value: "has_set_incoming_call_flow",
              },
            ]),

        {
          title: "Make and receive a call",
          subtitle:
            "Get started on PressOne today. Make and receive a call to and from anyone respectively.",
          buttonTitle: "Make a Call",
          img: "/assets/Manager/setup-status/test-call.svg",
          value: "has_made_call",
        },

        {
          title: "Watch a tour of the system",
          subtitle:
            "We have provided you with a short guide on how to use our system. Check it out now to get the best experience.",
          buttonTitle: "Watch Tour",
          img: "/assets/Manager/setup-status/tour.svg",
          value: "has_watched_tour",
        },
      ];
    },
  },
  actions: {
    clearStore() {
      this.isCompletingSetupFlow = false;
      this.isPublishingAudio = false;
      this.isPublished = false;
    },
    async fetchOnboardingStatus() {
      try {
        const response = await getListOfOnboardingStage();

        this.userOnboardingStatus = response;

        if (
          this.userOnboardingStatus &&
          this.userOnboardingStatus?.has_completed_onboarding_form &&
          this.hasFilledTypeForm
        ) {
          // Reset the filled state for an edge case where one switches to a new business
          this.hasFilledTypeForm = false;
          return true;
        }

        return false;
      } catch (error) {
        handleError({ error });

        return false;
      }
    },
  },

  persist: PINIA_PERSIST_OPTIONS,
});
